import { clamp } from '#shared'
import { z } from 'zod'

/** Keyboard and mouse actions a player can rebind. Pause stays on Escape. */
export const KEYBOARD_BINDABLES = [
  'moveForward',
  'moveBack',
  'moveLeft',
  'moveRight',
  'jump',
  'sprint',
  'interact',
  'skip',
  'useItem',
  'drop',
  'inventory',
  'phone',
] as const

export type KeyboardBindable = (typeof KEYBOARD_BINDABLES)[number]

/** The options a graphics preset owns. Editing one of them selects Custom. */
const PRESET_FIELDS = [
  'resolutionScale',
  'antiAliasing',
  'shadows',
  'ambientOcclusion',
  'reflections',
  'bloom',
] as const

const qualityLevel = ['off', 'low', 'high'] as const

/**
 * One table for the engine and the settings screen. `custom` is not a column:
 * it means the options no longer match any of these.
 */
export const QUALITY_PRESETS = {
  low: {
    resolutionScale: 0.5,
    antiAliasing: 'fxaa',
    shadows: 'off',
    ambientOcclusion: 'off',
    reflections: 'off',
    bloom: false,
  },
  medium: {
    resolutionScale: 0.75,
    antiAliasing: 'fxaa',
    shadows: 'low',
    ambientOcclusion: 'off',
    reflections: 'low',
    bloom: false,
  },
  high: {
    resolutionScale: 1,
    antiAliasing: 'msaa',
    shadows: 'low',
    ambientOcclusion: 'low',
    reflections: 'low',
    bloom: true,
  },
  ultra: {
    resolutionScale: 1,
    antiAliasing: 'msaa',
    shadows: 'high',
    ambientOcclusion: 'high',
    reflections: 'high',
    bloom: true,
  },
} as const

type PresetName = keyof typeof QUALITY_PRESETS

const HIGH = QUALITY_PRESETS.high

/** What the device looks like the first time settings are created. */
export interface SettingsEnv {
  readonly dev: boolean
  readonly touch: boolean
  readonly lowEnd: boolean
  readonly prefersReducedMotion: boolean
}

/** Invalid numbers become `fallback`, then clamp. They are never rejected. */
function ranged(min: number, max: number, fallback: number) {
  return z
    .number()
    .catch(fallback)
    .transform((value) => clamp(value, min, max))
}

/** Same as `ranged`, then snapped to `step` so a saved scale stays on the slider stops. */
function stepped(min: number, max: number, step: number, fallback: number) {
  return z
    .number()
    .catch(fallback)
    .transform((value) => {
      const snapped = Math.round(clamp(value, min, max) / step) * step
      return Math.round(snapped * 100) / 100
    })
}

function level(fallback: (typeof qualityLevel)[number]) {
  return z.enum(qualityLevel).catch(fallback)
}

function graphicsDefault() {
  return {
    preset: 'high' as const,
    renderer: 'webgl2' as const,
    resolutionScale: HIGH.resolutionScale,
    antiAliasing: HIGH.antiAliasing,
    shadows: HIGH.shadows,
    ambientOcclusion: HIGH.ambientOcclusion,
    reflections: HIGH.reflections,
    bloom: HIGH.bloom,
    fov: 90,
    frameRateLimit: 0 as const,
    showFps: false,
  }
}

function audioDefault() {
  return { master: 0.8, music: 0.6, sfx: 0.8, voice: 1 }
}

function controlsDefault() {
  return {
    mouseSensitivity: 1,
    gamepadSensitivity: 1,
    touchSensitivity: 1,
    invertY: false,
    keyboard: {},
  }
}

function accessibilityDefault() {
  return { subtitles: true, reducedMotion: false }
}

function devDefault() {
  return { enabled: false }
}

const graphicsSchema = z.object({
  preset: z.enum(['low', 'medium', 'high', 'ultra', 'custom']).catch('high'),
  renderer: z.enum(['webgl2', 'webgpu']).catch('webgl2'),
  resolutionScale: stepped(0.5, 1, 0.05, HIGH.resolutionScale),
  antiAliasing: z.enum(['fxaa', 'msaa']).catch(HIGH.antiAliasing),
  shadows: level(HIGH.shadows),
  ambientOcclusion: level(HIGH.ambientOcclusion),
  reflections: level(HIGH.reflections),
  bloom: z.boolean().catch(HIGH.bloom),
  fov: ranged(70, 110, 90),
  frameRateLimit: z.literal([0, 30, 60]).catch(0),
  showFps: z.boolean().catch(false),
})

const audioSchema = z.object({
  master: ranged(0, 1, 0.8),
  music: ranged(0, 1, 0.6),
  sfx: ranged(0, 1, 0.8),
  voice: ranged(0, 1, 1),
})

const keyboardSchema = z.partialRecord(z.enum(KEYBOARD_BINDABLES), z.array(z.string()).catch([]))

const controlsSchema = z.object({
  mouseSensitivity: ranged(0.1, 5, 1),
  gamepadSensitivity: ranged(0.1, 5, 1),
  touchSensitivity: ranged(0.1, 5, 1),
  invertY: z.boolean().catch(false),
  keyboard: keyboardSchema.catch(() => ({})).default(() => ({})),
})

const accessibilitySchema = z.object({
  subtitles: z.boolean().catch(true),
  reducedMotion: z.boolean().catch(false),
})

const devSchema = z.object({
  enabled: z.boolean().catch(false),
})

function canonicalSettings() {
  return {
    version: 1 as const,
    graphics: graphicsDefault(),
    audio: audioDefault(),
    controls: controlsDefault(),
    accessibility: accessibilityDefault(),
    dev: devDefault(),
  }
}

/**
 * Types, defaults and ranges for everything the player can change. Every leaf
 * catches a bad value, and each section catches a bad object, so one corrupt
 * field never wipes the rest. `parse` does not throw.
 */
export const SettingsSchema = z
  .object({
    version: z.literal(1).catch(1).default(1),
    // `.default` fills a missing section. `.catch` replaces one that isn't an object.
    graphics: graphicsSchema.catch(graphicsDefault).default(graphicsDefault),
    audio: audioSchema.catch(audioDefault).default(audioDefault),
    controls: controlsSchema.catch(controlsDefault).default(controlsDefault),
    accessibility: accessibilitySchema.catch(accessibilityDefault).default(accessibilityDefault),
    dev: devSchema.catch(devDefault).default(devDefault),
  })
  .catch(canonicalSettings)

export type Settings = z.infer<typeof SettingsSchema>
export type GraphicsSettings = Settings['graphics']
export type RendererKind = GraphicsSettings['renderer']

/** A settings group the screen can reset. `version` is not one of them. */
export type SettingsSection = Exclude<keyof Settings, 'version'>

/**
 * What `updateSettings` accepts. Sections are partial; `controls.keyboard` is the
 * whole override map, replaced rather than merged, so `{}` clears every override.
 */
export interface SettingsPatch {
  readonly graphics?: Partial<GraphicsSettings>
  readonly audio?: Partial<Settings['audio']>
  readonly controls?: Partial<Settings['controls']>
  readonly accessibility?: Partial<Settings['accessibility']>
  readonly dev?: Partial<Settings['dev']>
}

function presetFor(env: SettingsEnv): PresetName {
  if (env.touch) return 'low'
  if (env.lowEnd) return 'medium'
  return 'high'
}

/** The settings used when nothing is stored, chosen from the device. */
export function createDefaultSettings(env: SettingsEnv): Settings {
  const preset = presetFor(env)
  return {
    ...canonicalSettings(),
    graphics: {
      ...graphicsDefault(),
      preset,
      ...QUALITY_PRESETS[preset],
      frameRateLimit: env.touch ? 60 : 0,
    },
    accessibility: { subtitles: true, reducedMotion: env.prefersReducedMotion },
    dev: { enabled: env.dev },
  }
}

function applyGraphics(
  current: GraphicsSettings,
  patch: SettingsPatch['graphics'],
): GraphicsSettings {
  if (!patch) return current
  const graphics = { ...current, ...patch }
  const { preset } = patch
  if (preset !== undefined && preset !== 'custom') {
    return { ...graphics, ...QUALITY_PRESETS[preset], preset }
  }
  if (preset === undefined && PRESET_FIELDS.some((key) => patch[key] !== undefined)) {
    return { ...graphics, preset: 'custom' }
  }
  return graphics
}

/** Deep-merges `patch` into `settings`. Preset rules run after the merge. */
export function applyPatch(settings: Settings, patch: SettingsPatch): Settings {
  return {
    ...settings,
    ...(patch.graphics ? { graphics: applyGraphics(settings.graphics, patch.graphics) } : {}),
    ...(patch.audio ? { audio: { ...settings.audio, ...patch.audio } } : {}),
    ...(patch.controls ? { controls: { ...settings.controls, ...patch.controls } } : {}),
    ...(patch.accessibility
      ? { accessibility: { ...settings.accessibility, ...patch.accessibility } }
      : {}),
    ...(patch.dev ? { dev: { ...settings.dev, ...patch.dev } } : {}),
  }
}
